import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Progress } from '../components/ui/progress';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { 
  Database, 
  Server, 
  Clock, 
  AlertTriangle, 
  CheckCircle, 
  XCircle,
  RefreshCw,
  Cpu,
  HardDrive,
  Zap,
  TrendingUp,
  Calendar,
  Mail,
  Cloud,
  Info,
  GitBranch,
  MinusCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function SystemHealthPage() {
  const [healthData, setHealthData] = useState(null);
  const [errors, setErrors] = useState(null);
  const [apiMetrics, setApiMetrics] = useState(null);
  const [dbStats, setDbStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [lastRefresh, setLastRefresh] = useState(null);

  const fetchHealthData = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/system/health`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setHealthData(data);
      }
    } catch (error) {
      console.error('Error fetching health data:', error);
    }
  }, []);

  const fetchErrors = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/system/errors?hours=24`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setErrors(data);
      }
    } catch (error) {
      console.error('Error fetching errors:', error);
    }
  }, []);

  const fetchApiMetrics = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/system/api-metrics?minutes=60`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setApiMetrics(data);
      }
    } catch (error) {
      console.error('Error fetching API metrics:', error);
    }
  }, []);

  const fetchDbStats = useCallback(async () => {
    try {
      const response = await fetch(`${API_URL}/api/system/database-stats`, {
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setDbStats(data);
      }
    } catch (error) {
      console.error('Error fetching DB stats:', error);
    }
  }, []);

  const refreshAll = async () => {
    setRefreshing(true);
    await Promise.all([
      fetchHealthData(),
      fetchErrors(),
      fetchApiMetrics(),
      fetchDbStats()
    ]);
    setLastRefresh(new Date());
    setRefreshing(false);
  };

  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      await refreshAll();
      setLoading(false);
    };
    loadData();
    
    // Auto-refresh every 30 seconds
    const interval = setInterval(refreshAll, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Stati osservabilità: OK / Warning / Unavailable / Error.
  // Invariante: UNKNOWN != HEALTHY → tutto ciò che non è osservabile = Unavailable (grigio), mai verde.
  const LEVEL_META = {
    ok: { badge: 'bg-green-100 text-green-800', dot: 'bg-green-500', label: 'OK' },
    warning: { badge: 'bg-yellow-100 text-yellow-800', dot: 'bg-yellow-500', label: 'Warning' },
    unavailable: { badge: 'bg-gray-100 text-gray-600', dot: 'bg-gray-400', label: 'Non disponibile' },
    error: { badge: 'bg-red-100 text-red-800', dot: 'bg-red-500', label: 'Error' },
  };
  const levelMeta = (lvl) => LEVEL_META[lvl] || LEVEL_META.unavailable;
  const LevelBadge = ({ level, text }) => {
    const m = levelMeta(level);
    const Icon = level === 'ok' ? CheckCircle : level === 'error' ? XCircle
      : level === 'warning' ? AlertTriangle : MinusCircle;
    return (
      <Badge className={m.badge} data-testid={`level-badge-${level}`}>
        <Icon className="w-3 h-3 mr-1" />{text || m.label}
      </Badge>
    );
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64" data-testid="system-health-loading">
        <RefreshCw className="w-8 h-8 animate-spin text-orange-500" />
        <span className="ml-2 text-gray-600">Caricamento dati sistema...</span>
      </div>
    );
  }

  return (
    <div className="space-y-6 p-6" data-testid="system-health-page">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">System Health Dashboard</h1>
          <p className="text-gray-500">Monitoraggio in tempo reale del sistema ABITALO CRM</p>
        </div>
        <div className="flex items-center gap-4">
          {lastRefresh && (
            <span className="text-sm text-gray-500">
              Ultimo aggiornamento: {lastRefresh.toLocaleTimeString('it-IT')}
            </span>
          )}
          <Button 
            onClick={refreshAll} 
            disabled={refreshing}
            variant="outline"
            data-testid="refresh-btn"
          >
            <RefreshCw className={`w-4 h-4 mr-2 ${refreshing ? 'animate-spin' : ''}`} />
            Aggiorna
          </Button>
        </div>
      </div>

      {/* ===== APPLICATION HEALTH ===== */}
      <div data-testid="application-health-section">
        <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-400 mb-3">Application Health</h2>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <Card data-testid="svc-backend-card">
            <CardContent className="p-4">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3">
                  <div className="p-2 bg-green-100 rounded-lg"><Server className="w-5 h-5 text-green-600" /></div>
                  <div>
                    <p className="font-medium text-gray-900">Backend</p>
                    <p className="text-xs text-gray-500">Uptime {healthData?.uptime?.uptime_formatted || '—'}</p>
                  </div>
                </div>
                <LevelBadge level="ok" text="OK" />
              </div>
            </CardContent>
          </Card>
          {healthData?.services?.map((s, i) => {
            const Icon = s.name === 'MongoDB' ? Database : s.name === 'APScheduler' ? Clock
              : s.name === 'Email' ? Mail : s.name === 'Google Calendar' ? Calendar : Cloud;
            const badgeText = s.name === 'Google Calendar' && s.level === 'unavailable' ? 'Non connesso'
              : s.status === 'configured' ? 'Configurato'
              : s.status === 'online' ? 'Online' : undefined;
            return (
              <Card key={i} data-testid={`svc-${s.name.replace(/\s+/g, '-').toLowerCase()}-card`}>
                <CardContent className="p-4">
                  <div className="flex items-start justify-between">
                    <div className="flex items-center gap-3">
                      <div className="p-2 bg-gray-100 rounded-lg"><Icon className="w-5 h-5 text-gray-600" /></div>
                      <div>
                        <p className="font-medium text-gray-900">{s.name}{s.provider ? ` · ${s.provider}` : ''}</p>
                        {s.name === 'MongoDB' && s.ping_ms !== undefined && <p className="text-xs text-gray-500">Ping {s.ping_ms} ms</p>}
                        {s.name === 'APScheduler' && <p className="text-xs text-gray-500">{s.jobs} job attivi</p>}
                        {s.name === 'Email' && s.host && <p className="text-xs text-gray-500">{s.host}</p>}
                        {s.name === 'Google Calendar' && <p className="text-xs text-gray-500">{s.connected_users} utenti connessi{s.optional ? ' · opzionale' : ''}</p>}
                      </div>
                    </div>
                    <LevelBadge level={s.level} text={badgeText} />
                  </div>
                  {s.name === 'APScheduler' && s.jobs_detail?.length > 0 && (
                    <div className="mt-3 pt-3 border-t border-gray-100 space-y-1" data-testid="scheduler-jobs-detail">
                      {s.jobs_detail.map((j, k) => (
                        <div key={k} className="flex items-center justify-between text-xs">
                          <span className="text-gray-600 truncate mr-2">{j.name}</span>
                          <span className="text-gray-400 whitespace-nowrap">{j.next_run ? new Date(j.next_run).toLocaleString('it-IT') : '—'}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </CardContent>
              </Card>
            );
          })}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-4">
          <Card data-testid="kpi-uptime-backend"><CardContent className="p-4">
            <div className="flex items-center gap-3"><div className="p-2 bg-green-100 rounded-lg"><Clock className="w-5 h-5 text-green-600" /></div>
            <div><p className="text-sm text-gray-500">Uptime backend</p><p className="text-xl font-bold text-gray-900">{healthData?.uptime?.uptime_formatted || '—'}</p></div></div>
          </CardContent></Card>
          <Card data-testid="kpi-api-response"><CardContent className="p-4">
            <div className="flex items-center gap-3"><div className="p-2 bg-blue-100 rounded-lg"><Zap className="w-5 h-5 text-blue-600" /></div>
            <div><p className="text-sm text-gray-500">API response (avg · 60m)</p><p className="text-xl font-bold text-gray-900">{apiMetrics?.total_requests > 0 ? `${apiMetrics.avg_response_ms} ms` : 'Non disponibile'}</p></div></div>
          </CardContent></Card>
          <Card data-testid="kpi-error-rate"><CardContent className="p-4">
            <div className="flex items-center gap-3"><div className={`p-2 rounded-lg ${apiMetrics?.error_rate_5xx > 0 ? 'bg-red-100' : 'bg-green-100'}`}><AlertTriangle className={`w-5 h-5 ${apiMetrics?.error_rate_5xx > 0 ? 'text-red-600' : 'text-green-600'}`} /></div>
            <div><p className="text-sm text-gray-500">Error rate 5xx (60m)</p><p className="text-xl font-bold text-gray-900">{apiMetrics?.total_requests > 0 ? `${apiMetrics.error_rate_5xx}%` : 'Non disponibile'}</p></div></div>
          </CardContent></Card>
        </div>
      </div>

      {/* ===== INFRASTRUCTURE ===== */}
      <div data-testid="infrastructure-section">
        <div className="flex flex-wrap items-center justify-between gap-2 mb-3">
          <h2 className="text-xs font-semibold uppercase tracking-wider text-gray-400">Infrastructure</h2>
          {(healthData?.system_resources?.runtime_quota?.vcpu || healthData?.system_resources?.runtime_quota?.ram_gb) && (
            <span className="text-xs text-gray-500" data-testid="runtime-quota" title="Quota realmente rilevata dal runtime (cgroup v2), non il nome del piano">
              Quota runtime rilevata: <b className="text-gray-700">
                {healthData.system_resources.runtime_quota.vcpu ? `${healthData.system_resources.runtime_quota.vcpu} vCPU` : 'vCPU n/d'}
                {healthData.system_resources.runtime_quota.ram_gb ? ` · ${healthData.system_resources.runtime_quota.ram_gb} GB RAM` : ''}
              </b>
            </span>
          )}
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <Card data-testid="cpu-card"><CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3"><div className="p-2 bg-blue-100 rounded-lg"><Cpu className="w-5 h-5 text-blue-600" /></div>
              <div><p className="text-sm text-gray-500">CPU applicazione</p>
              <p className="text-xl font-bold text-gray-900">{healthData?.system_resources?.cpu?.percent != null ? `${healthData.system_resources.cpu.percent}%` : 'Non disponibile'}</p></div></div>
              <LevelBadge level={healthData?.system_resources?.cpu?.level} />
            </div>
            {healthData?.system_resources?.cpu?.percent != null && <Progress value={healthData.system_resources.cpu.percent} className="mt-2 h-2" />}
            <div className="flex items-center justify-between mt-1">
              <p className="text-xs text-gray-400" data-testid="cpu-cores-used">{healthData?.system_resources?.cpu?.cores_used != null && healthData?.system_resources?.cpu?.vcpu
                ? `${healthData.system_resources.cpu.cores_used.toFixed(2).replace('.', ',')} / ${healthData.system_resources.cpu.vcpu.toFixed(2).replace('.', ',')} vCPU`
                : (healthData?.system_resources?.cpu?.note || 'quota non disponibile')}</p>
              {healthData?.system_resources?.cpu?.samples != null && (
                <p className="text-xs text-gray-500" data-testid="cpu-avg-60s">media 60s: <b>{healthData.system_resources.cpu.avg_60s != null ? `${healthData.system_resources.cpu.avg_60s}%` : 'dati insufficienti'}</b></p>
              )}
            </div>
          </CardContent></Card>
          <Card data-testid="memory-card"><CardContent className="p-4">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3"><div className="p-2 bg-purple-100 rounded-lg"><Server className="w-5 h-5 text-purple-600" /></div>
              <div><p className="text-sm text-gray-500">Memoria applicazione</p>
              <p className="text-xl font-bold text-gray-900">{healthData?.system_resources?.memory?.percent != null ? `${healthData.system_resources.memory.percent}%` : 'Non disponibile'}</p></div></div>
              <LevelBadge level={healthData?.system_resources?.memory?.level} />
            </div>
            {healthData?.system_resources?.memory?.percent != null && <Progress value={healthData.system_resources.memory.percent} className="mt-2 h-2" />}
            <p className="text-xs text-gray-400 mt-1">{healthData?.system_resources?.memory?.limit_mb != null
              ? `${healthData.system_resources.memory.used_mb} MB / ${healthData.system_resources.memory.limit_mb} MB`
              : (healthData?.system_resources?.memory?.used_mb != null
                  ? `${healthData.system_resources.memory.used_mb} MB · ${healthData?.system_resources?.memory?.note || 'Limite non disponibile dal runtime'}`
                  : (healthData?.system_resources?.memory?.note || 'Non disponibile'))}</p>
          </CardContent></Card>
          <Card data-testid="storage-db-card"><CardContent className="p-4">
            <div className="flex items-center gap-3"><div className="p-2 bg-orange-100 rounded-lg"><HardDrive className="w-5 h-5 text-orange-600" /></div>
            <div><p className="text-sm text-gray-500">Dimensione database</p>
            <p className="text-xl font-bold text-gray-900">{dbStats?.total_size_mb != null ? `${dbStats.total_size_mb} MB` : (healthData?.database?.size_mb != null ? `${healthData.database.size_mb} MB` : 'Non disponibile')}</p></div></div>
            <p className="text-xs text-gray-400 mt-1" title="Dati da MongoDB dbStats">{healthData?.database?.collections_count != null ? `${healthData.database.collections_count} collection · dati da MongoDB dbStats` : 'Dati da MongoDB dbStats'}</p>
          </CardContent></Card>
        </div>
      </div>

      {/* ===== VERSION ===== */}
      <Card data-testid="version-section"><CardContent className="p-4">
        <div className="flex flex-wrap items-center gap-x-8 gap-y-2 text-sm">
          <span className="flex items-center gap-2 text-gray-500"><Info className="w-4 h-4" />Versione</span>
          <span className="text-gray-700">Environment: <b>{healthData?.version?.environment || 'Non disponibile'}</b></span>
          <span className="text-gray-700 flex items-center gap-1"><GitBranch className="w-3.5 h-3.5 text-gray-400" />Commit: <b className="font-mono">{healthData?.version?.commit || 'Non disponibile'}</b></span>
          <span className="text-gray-700">Backend avviato: <b>{healthData?.version?.backend_started_at ? new Date(healthData.version.backend_started_at).toLocaleString('it-IT') : 'Non disponibile'}</b></span>
        </div>
      </CardContent></Card>

      <Tabs defaultValue="database" className="space-y-4">
        <TabsList>
          <TabsTrigger value="database">Database</TabsTrigger>
          <TabsTrigger value="api">Performance API</TabsTrigger>
          <TabsTrigger value="errors">Errori</TabsTrigger>
        </TabsList>

        {/* Database Tab */}
        <TabsContent value="database" data-testid="database-tab">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Database className="w-5 h-5" />
                Database Statistics
              </CardTitle>
              <CardDescription>
                {dbStats?.database_name} - {dbStats?.total_size_mb} MB totali
              </CardDescription>
            </CardHeader>
            <CardContent>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
                <div className="p-4 bg-blue-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-blue-600">{dbStats?.collections_count || 0}</p>
                  <p className="text-sm text-gray-600">Collections</p>
                </div>
                <div className="p-4 bg-green-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-green-600">
                    {(dbStats?.total_documents || 0).toLocaleString('it-IT')}
                  </p>
                  <p className="text-sm text-gray-600">Documenti Totali</p>
                </div>
                <div className="p-4 bg-purple-50 rounded-lg text-center">
                  <p className="text-2xl font-bold text-purple-600">{dbStats?.index_size_mb || 0} MB</p>
                  <p className="text-sm text-gray-600">Indici</p>
                </div>
              </div>

              <h4 className="font-medium text-gray-900 mb-3">Top Collections</h4>
              <div className="space-y-2">
                {dbStats?.collections?.slice(0, 10).map((coll, index) => (
                  <div 
                    key={index}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded"
                  >
                    <div>
                      <p className="font-medium text-gray-900">{coll.name}</p>
                      <p className="text-xs text-gray-500">{coll.indexes || 0} indici</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-gray-900">
                        {(coll.documents || 0).toLocaleString('it-IT')} docs
                      </p>
                      <p className="text-xs text-gray-500">{coll.size_kb || 0} KB</p>
                    </div>
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* API Performance Tab */}
        <TabsContent value="api" data-testid="api-tab">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Zap className="w-5 h-5" />
                Performance API
              </CardTitle>
              <CardDescription>
                Finestra: ultimi {apiMetrics?.period_minutes || 60} minuti · {apiMetrics?.total_requests || 0} richieste
              </CardDescription>
            </CardHeader>
            <CardContent>
              {apiMetrics?.total_requests > 0 ? (
                <>
                  <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4 mb-6">
                    <div className="p-4 bg-orange-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-orange-600">{apiMetrics?.total_requests || 0}</p>
                      <p className="text-sm text-gray-600">Request count</p>
                    </div>
                    <div className="p-4 bg-blue-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-blue-600">{apiMetrics?.avg_response_ms ?? 0} ms</p>
                      <p className="text-sm text-gray-600">Avg response</p>
                    </div>
                    <div className="p-4 bg-indigo-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-indigo-600">{apiMetrics?.p50_response_ms ?? 'N/A'} ms</p>
                      <p className="text-sm text-gray-600">P50</p>
                    </div>
                    <div className="p-4 bg-purple-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-purple-600">{apiMetrics?.p95_response_ms ?? 'N/A'} ms</p>
                      <p className="text-sm text-gray-600">P95</p>
                    </div>
                    <div className="p-4 bg-green-50 rounded-lg text-center">
                      <p className="text-2xl font-bold text-green-600">{apiMetrics?.success_rate ?? 0}%</p>
                      <p className="text-sm text-gray-600">Success rate</p>
                    </div>
                    <div className={`p-4 rounded-lg text-center ${apiMetrics?.error_rate_5xx > 0 ? 'bg-red-50' : 'bg-gray-50'}`}>
                      <p className={`text-2xl font-bold ${apiMetrics?.error_rate_5xx > 0 ? 'text-red-600' : 'text-gray-600'}`}>{apiMetrics?.error_rate_5xx ?? 0}%</p>
                      <p className="text-sm text-gray-600">5xx rate</p>
                    </div>
                  </div>

                  {apiMetrics?.endpoints && Object.keys(apiMetrics.endpoints).length > 0 && (
                    <>
                      <h4 className="font-medium text-gray-900 mb-3">Top Endpoints</h4>
                      <div className="space-y-2">
                        {Object.entries(apiMetrics.endpoints).slice(0, 10).map(([endpoint, stats]) => (
                          <div 
                            key={endpoint}
                            className="flex items-center justify-between p-3 bg-gray-50 rounded"
                          >
                            <div>
                              <p className="font-mono text-sm text-gray-900">{endpoint}</p>
                              <p className="text-xs text-gray-500">
                                {stats.count} richieste | {stats.errors} errori
                              </p>
                            </div>
                            <div className="text-right">
                              <p className="font-medium text-gray-900">{stats.avg_time_ms} ms</p>
                              <p className="text-xs text-gray-500">avg</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </>
                  )}
                </>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <TrendingUp className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                  <p>Nessuna metrica API disponibile</p>
                  <p className="text-sm">Le metriche verranno raccolte automaticamente</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Errors Tab */}
        <TabsContent value="errors" data-testid="errors-tab">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <AlertTriangle className="w-5 h-5 text-yellow-500" />
                Errori Recenti
              </CardTitle>
              <CardDescription>Errori 5xx e log di errore delle ultime {errors?.period_hours || 24} ore (sanitizzati)</CardDescription>
            </CardHeader>
            <CardContent>
              {(errors?.recent_5xx?.length > 0 || errors?.audit_errors?.length > 0) ? (
                <div className="space-y-6">
                  {errors?.recent_5xx?.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-3">Errori 5xx recenti ({errors.total_5xx})</h4>
                      <div className="space-y-2" data-testid="recent-5xx-list">
                        {errors.recent_5xx.map((e, i) => (
                          <div key={i} className="flex items-center justify-between p-3 bg-red-50 border border-red-100 rounded-lg">
                            <div className="flex items-center gap-3 min-w-0">
                              <Badge className="bg-red-100 text-red-800 shrink-0">{e.status_code}</Badge>
                              <span className="font-mono text-sm text-gray-700 truncate">{e.method} {e.endpoint}</span>
                            </div>
                            <span className="text-xs text-gray-500 whitespace-nowrap ml-3">{e.timestamp && new Date(e.timestamp).toLocaleString('it-IT')}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                  {errors?.audit_errors?.length > 0 && (
                    <div>
                      <h4 className="font-medium text-gray-900 mb-3">Log di errore (audit)</h4>
                      <div className="space-y-2" data-testid="audit-errors-list">
                        {errors.audit_errors.map((e, i) => (
                          <div key={i} className="flex items-start justify-between p-3 bg-yellow-50 border border-yellow-100 rounded-lg">
                            <div className="min-w-0">
                              <p className="text-sm font-medium text-yellow-800">{e.action || 'error'}{e.status_code ? ` · ${e.status_code}` : ''}</p>
                              <p className="text-xs text-gray-600 mt-0.5 truncate">
                                {e.endpoint ? <span className="font-mono">{e.endpoint}</span> : null}
                                {e.entity_type ? ` ${e.entity_type}${e.entity_id ? `:${e.entity_id}` : ''}` : ''}
                                {e.correlation_id ? ` · id ${e.correlation_id}` : ''}
                              </p>
                            </div>
                            <span className="text-xs text-gray-500 whitespace-nowrap ml-3">{e.timestamp && new Date(e.timestamp).toLocaleString('it-IT')}</span>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <CheckCircle className="w-12 h-12 mx-auto mb-4 text-green-300" />
                  <p>Nessun errore 5xx nelle ultime {errors?.period_hours || 24} ore</p>
                  <p className="text-sm">Il sistema funziona correttamente</p>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>
    </div>
  );
}
